import React, { useState, useEffect } from 'react';
import { useAddLoanMutation } from '../../services/loanApiSlice';
import { useLocation } from 'react-router-dom';
import { 
  FaUser, 
  FaFileAlt, 
  FaCalendarAlt, 
  FaMoneyBillWave, 
  FaPercent, 
  FaClock, 
  FaCalculator,
  FaDollarSign,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSync,
  FaTrashAlt,
  FaPaperPlane,
  FaInfoCircle
} from 'react-icons/fa';

const AddLoans = () => {
  const [addLoan, { isLoading: isAddingLoan, error: addLoanError }] = useAddLoanMutation();
  const location = useLocation();

  // Initialize form state with empty values (allow manual entry)
  const [formData, setFormData] = useState({
    borrowerId: '',
    loanProductId: '',
    principalAmount: '',
    interestRate: '',
    termMonths: '',
    startDate: new Date().toISOString().split('T')[0],
    totalPayable: '',
    monthlyPayable: ''
  });

  // Retrieve data from navigation state
  useEffect(() => {
    if (location.state?.loanData) {
      const { loanData, applicationId } = location.state;
      
      // Populate form with data from loan application
      setFormData(prev => ({
        ...prev,
        borrowerId: loanData.borrowerId.toString(),
        loanProductId: loanData.loanProductId.toString(),
        principalAmount: loanData.principalAmount.toString(),
        interestRate: loanData.interestRate.toString(),
        termMonths: loanData.termMonths.toString(),
        startDate: loanData.startDate,
        totalPayable: loanData.totalPayable
      }));

      // Log the received data for debugging
      console.log('Received from Approvals - Application ID:', applicationId);
      console.log('Received from Approvals - Loan Data:', loanData);
    }
  }, [location.state]);

  const [formStatus, setFormStatus] = useState({
    isSubmitted: false,
    success: false,
    message: ''
  });

  // Calculate totals whenever principal, interest rate, or term changes
  useEffect(() => {
    calculateTotals();
  }, [formData.principalAmount, formData.interestRate, formData.termMonths]);

  const calculateTotals = () => {
    const principal = parseFloat(formData.principalAmount) || 0;
    const rate = parseFloat(formData.interestRate) || 0;
    const term = parseInt(formData.termMonths) || 0;

    if (principal > 0 && rate > 0 && term > 0) {
      // Formula: Total Payable = Principal + (Principal × Interest Rate × Term/12)
      const totalInterest = principal * (rate / 100) * (term / 12);
      const totalPayable = principal + totalInterest;
      const monthlyPayable = term > 0 ? totalPayable / term : 0;

      setFormData(prev => ({
        ...prev,
        totalPayable: totalPayable.toFixed(2),
        monthlyPayable: monthlyPayable.toFixed(2)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        totalPayable: '',
        monthlyPayable: ''
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For numeric fields, ensure they're valid numbers
    if (['principalAmount', 'interestRate'].includes(name)) {
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else if (name === 'termMonths') {
      if (value === '' || /^\d*$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare loan data for submission
    const loanData = {
      borrowerId: parseInt(formData.borrowerId),
      loanProductId: parseInt(formData.loanProductId),
      principalAmount: parseFloat(formData.principalAmount),
      interestRate: parseFloat(formData.interestRate),
      termMonths: parseInt(formData.termMonths),
      startDate: formData.startDate,
      totalPayable: parseFloat(formData.totalPayable || 0)
    };

    try {
      // Call the AddLoan mutation
      const response = await addLoan(loanData).unwrap();
      
      // Check the response structure
      if (response.StatusCode === 200) {
        setFormStatus({
          isSubmitted: true,
          success: true,
          message: response.Result || 'Loan added successfully!'
        });
        
        // Reset form after successful submission
        setTimeout(() => {
          setFormData({
            borrowerId: '',
            loanProductId: '',
            principalAmount: '',
            interestRate: '',
            termMonths: '',
            startDate: new Date().toISOString().split('T')[0],
            totalPayable: '',
            monthlyPayable: ''
          });
          setFormStatus({
            isSubmitted: false,
            success: false,
            message: ''
          });
        }, 3000);
      } else if (response.StatusCode === -1) {
        setFormStatus({
          isSubmitted: true,
          success: false,
          message: response.Result || 'Failed to add loan. Please check your inputs.'
        });
      } else {
        setFormStatus({
          isSubmitted: true,
          success: false,
          message: response.Result || 'Failed to add loan. Please try again.'
        });
      }
    } catch (err) {
      console.error('Failed to add loan:', err);
      
      // Handle specific error messages from API
      let errorMessage = 'An error occurred while adding the loan. Please try again.';
      
      if (err?.data?.StatusCode === -1) {
        errorMessage = err.data.Result || 'Failed to add loan. Please check your inputs.';
      } else if (err?.data?.Result) {
        errorMessage = err.data.Result;
      }
      
      setFormStatus({
        isSubmitted: true,
        success: false,
        message: errorMessage
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-lg mb-4">
            <FaMoneyBillWave className="text-3xl text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Create New Loan
          </h1>
          <p className="mt-3 text-lg text-gray-600 max-w-2xl mx-auto">
            Fill in the loan details below to create a new loan record. All fields marked with <span className="text-red-500">*</span> are required.
          </p>
          
          {/* Status Messages */}
          {formStatus.isSubmitted && (
            <div className={`mt-6 max-w-2xl mx-auto rounded-xl p-5 shadow-lg border-l-4 ${
              formStatus.success 
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-500' 
                : 'bg-gradient-to-r from-red-50 to-pink-50 border-red-500'
            }`}>
              <div className="flex items-center">
                {formStatus.success ? (
                  <FaCheckCircle className="text-2xl text-green-600 mr-3" />
                ) : (
                  <FaExclamationTriangle className="text-2xl text-red-600 mr-3" />
                )}
                <p className={`text-sm font-medium ${
                  formStatus.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {formStatus.message}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Form Card */}
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Borrower Information */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <FaUser className="mr-2 text-blue-600" />
                      Borrower Information
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="borrowerId" className="block text-sm font-medium text-gray-700 mb-1">
                          Borrower ID <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="borrowerId"
                          name="borrowerId"
                          required
                          disabled='true'
                          value={formData.borrowerId}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                          placeholder="Enter borrower ID"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Loan Product Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <FaFileAlt className="mr-2 text-indigo-600" />
                      Loan Product Information
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="loanProductId" className="block text-sm font-medium text-gray-700 mb-1">
                          Loan Product ID <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="loanProductId"
                          name="loanProductId"
                          required
                          disabled= "true"
                          value={formData.loanProductId}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                          placeholder="Enter loan product ID"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Loan Details */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <FaCalculator className="mr-2 text-purple-600" />
                      Loan Details
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="principalAmount" className="block text-sm font-medium text-gray-700 mb-1">
                          Principal Amount ($) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="principalAmount"
                          name="principalAmount"
                          required
                          disabled='true'
                          value={formData.principalAmount}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label htmlFor="interestRate" className="block text-sm font-medium text-gray-700 mb-1">
                          Annual Interest Rate (%) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="interestRate"
                          name="interestRate"
                          required
                          disabled='true'
                          value={formData.interestRate}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                          placeholder="e.g., 5.5"
                        />
                      </div>

                      <div>
                        <label htmlFor="termMonths" className="block text-sm font-medium text-gray-700 mb-1">
                          Loan Term (Months) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="termMonths"
                          name="termMonths"
                          required
                          disabled='true'
                          value={formData.termMonths}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                          placeholder="e.g., 36"
                        />
                      </div>

                      <div>
                        <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                          Start Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          id="startDate"
                          name="startDate"
                          required
                          disabled='true'
                          value={formData.startDate}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Calculation Results */}
              {(formData.totalPayable || formData.monthlyPayable) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-blue-50 rounded-xl border border-blue-200">
                  <div>
                    <div className="text-sm font-medium text-gray-600">Total Payable Amount</div>
                    <div className="text-2xl font-bold text-gray-900">
                      ${formData.totalPayable || '0.00'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Principal + Interest over loan term
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-600">Monthly Payment</div>
                    <div className="text-2xl font-bold text-gray-900">
                      ${formData.monthlyPayable || '0.00'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Total payable divided by {formData.termMonths || 0} months
                    </div>
                  </div>
                </div>
              )}

              {/* Formula Display */}
              <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                <div className="text-sm font-medium text-gray-700 mb-3">Calculation Formula:</div>
                <div className="text-xs text-gray-600 space-y-2">
                  <div>
                    <code className="bg-gray-100 px-2 py-1 rounded">
                      Total Payable = Principal + (Principal × Interest Rate × Term/12)
                    </code>
                  </div>
                  <div className="text-gray-500">
                    Monthly Payment = Total Payable ÷ Loan Term (Months)
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setFormData({
                    borrowerId: '',
                    loanProductId: '',
                    principalAmount: '',
                    interestRate: '',
                    termMonths: '',
                    startDate: new Date().toISOString().split('T')[0],
                    totalPayable: '',
                    monthlyPayable: ''
                  })}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200 flex items-center gap-2"
                >
                  <FaTrashAlt className="text-sm" />
                  Clear Form
                </button>
                <button
                  type="submit"
                  disabled={isAddingLoan}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAddingLoan ? (
                    <span><FaSync className="text-sm animate-spin" /> Creating...</span>
                  ) : (
                    <span><FaPaperPlane className="text-sm" /> Create Loan</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Information Panel */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="text-sm font-medium text-blue-800 mb-4 flex items-center">
            <FaInfoCircle className="mr-2" />
            Important Notes:
          </h3>
          <ul className="text-xs text-blue-700 space-y-2">
            <li>• All fields marked with <span className="text-red-500">*</span> are required</li>
            <li>• Interest rate should be entered as an annual percentage (e.g., 5 for 5%)</li>
            <li>• Start date defaults to today's date but can be modified</li>
            <li>• Total payable and monthly payment are calculated automatically</li>
            <li>• The system will validate borrower and loan product IDs before creating the loan</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AddLoans;
